import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { CheckCircle2, Download, Loader2, Receipt, X } from "lucide-react";
import toast from "react-hot-toast";

import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { bandejaKeys } from "@/lib/queryKeys";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import {
  accountantBulkAcceptRecibidas,
  accountantExportRecibidasZip,
  getAccountantInboxAcks,
} from "@/services/dashboardService";
import {
  BandejaRow,
  BandejaRowList,
} from "@/components/features/accountant/BandejaRow";
import { BANDEJA_SECTIONS } from "@/components/features/accountant/BandejaRowMeta";
import {
  BANDEJA_TENANT_FILTER_ALL,
  BandejaFilterBar,
  tenantDisplayName,
} from "@/components/features/accountant/BandejaFilterBar";
import type { AccountantInboxAckItem } from "@fiscalo/shared-types";

const PAGE_LIMIT_INITIAL = 25;
const PAGE_LIMIT_MAX = 100;

/** Stable client-side key — same shape the bulk endpoint expects. */
function keyOf(tenantId: string, recibidaId: string): string {
  return `${tenantId}:${recibidaId}`;
}

export function BandejaAcksPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();
  const [limit, setLimit] = useState(PAGE_LIMIT_INITIAL);
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeTenant } = useTenantSwitcher();
  /** Filter state lives in the URL so the accountant can bookmark a
   *  view (e.g. "/inbox/acks?tenant=acme&overdue=true") and reload
   *  without losing it. Single-source-of-truth pattern matches the
   *  invoices page.
   *
   *  Default scope: when the accountant is inside a specific client
   *  (activeTenant set — i.e. impersonating), the bandeja auto-scopes
   *  to that client so it doesn't surface every other tenant's work.
   *  Picking "Todos" (an explicit ?tenant=all) opts back into the
   *  cartera-wide view. The tenant is also sent to the server so the
   *  scope is correct regardless of pagination. */
  const defaultTenantFilter = activeTenant?.id ?? BANDEJA_TENANT_FILTER_ALL;
  const tenantFilter = searchParams.get("tenant") ?? defaultTenantFilter;
  const overdueOnly = searchParams.get("overdue") === "true";
  const searchQuery = searchParams.get("q") ?? "";
  /** Set of `${tenantId}:${recibidaId}` keys currently selected. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /**
   * Keys whose row is currently fading out as part of a bulk-accept
   * burst. Driven separately from `selected` so the rows animate
   * out cleanly even if React Query refetches and clears `selected`.
   */
  const [animatingOut, setAnimatingOut] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: bandejaKeys.acks({ limit, tenant: tenantFilter }),
    queryFn: () =>
      getAccountantInboxAcks({
        limit,
        tenantId:
          tenantFilter === BANDEJA_TENANT_FILTER_ALL ? undefined : tenantFilter,
      }),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const items = data?.items ?? [];

  /**
   * Tenants present in the current queue. Built from the loaded items
   * rather than the full cartera so the dropdown only lists clients
   * that actually have pending ACKs — selecting "Tenant X with no
   * pending work" would be confusing. Sorted alphabetically.
   */
  const tenantOptions = useMemo(() => {
    const seen = new Map<string, AccountantInboxAckItem["tenant"]>();
    for (const it of items) {
      if (!seen.has(it.tenant.id)) seen.set(it.tenant.id, it.tenant);
    }
    return Array.from(seen.values()).sort((a, b) =>
      tenantDisplayName(a).localeCompare(tenantDisplayName(b)),
    );
  }, [items]);

  /**
   * Filter pipeline: tenant scope → overdue toggle → free-text. Applied
   * client-side since the page already loads up to 100 items, and
   * bandeja UX demands instant feedback as the accountant narrows down.
   * Search matches tenant display name, the issuer (rucEmisor +
   * legalName) and the CFE id (serie + nro) so the accountant can
   * type any one of those to find a row.
   */
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (
        tenantFilter !== BANDEJA_TENANT_FILTER_ALL &&
        it.tenant.id !== tenantFilter
      ) {
        return false;
      }
      if (overdueOnly && !it.overdue) return false;
      if (normalizedQuery) {
        const haystack = [
          tenantDisplayName(it.tenant),
          it.rucEmisor,
          it.rucEmisorLegalName ?? "",
          it.serie,
          String(it.nro),
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(normalizedQuery)) return false;
      }
      return true;
    });
  }, [items, tenantFilter, overdueOnly, normalizedQuery]);

  const hasActiveFilters =
    tenantFilter !== defaultTenantFilter ||
    overdueOnly ||
    normalizedQuery.length > 0;

  // Stable visible keys — drives the "select all" indeterminate state
  // and the bulk-accept payload assembly. Tied to *filtered* items so
  // the bulk operation only touches what the accountant currently sees.
  const visibleKeys = useMemo(
    () => filteredItems.map((i) => keyOf(i.tenant.id, i.recibidaId)),
    [filteredItems],
  );

  /**
   * Effective selection respects the current filter. Selections from
   * items that filtered out remain in `selected` (no destructive
   * change on filter toggle), but the bulk bar and "select all"
   * indeterminate state only count what's currently visible — keeps
   * the displayed count honest. The bulk-accept payload is also
   * derived from filteredItems for the same reason.
   */
  const effectiveSelectedCount = useMemo(
    () => visibleKeys.filter((k) => selected.has(k)).length,
    [visibleKeys, selected],
  );

  const setTenantFilter = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Always write the choice explicitly. Deleting the param would make
          // it fall back to the active-tenant default, so "Todos" must persist
          // as ?tenant=all to override that default.
          next.set("tenant", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleOverdueFilter = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (overdueOnly) next.delete("overdue");
        else next.set("overdue", "true");
        return next;
      },
      { replace: true },
    );
  }, [overdueOnly, setSearchParams]);

  const setSearchQuery = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value.trim() === "") next.delete("q");
          else next.set("q", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tenant");
        next.delete("overdue");
        next.delete("q");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const allVisibleSelected =
    visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));
  const someVisibleSelected =
    !allVisibleSelected && visibleKeys.some((k) => selected.has(k));

  const toggleOne = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllVisible = (): void => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        // Drop every visible key from the selection (keep any keys
        // that came from older pages no longer rendered — unlikely
        // but defensive).
        const next = new Set(prev);
        for (const k of visibleKeys) next.delete(k);
        return next;
      }
      const next = new Set(prev);
      for (const k of visibleKeys) next.add(k);
      return next;
    });
  };

  const clearSelection = (): void => setSelected(new Set());

  const bulkMutation = useMutation({
    mutationFn: accountantBulkAcceptRecibidas,
    onMutate: (vars) => {
      // Optimistically begin fading the rows we're about to accept.
      const keys = new Set(
        vars.items.map((i) => keyOf(i.tenantId, i.recibidaId)),
      );
      setAnimatingOut((prev) => {
        const next = new Set(prev);
        for (const k of keys) next.add(k);
        return next;
      });
      return { startedKeys: keys };
    },
    onSuccess: async (resp, _vars, ctx) => {
      const acceptedKeys = new Set(
        resp.results
          .filter((r) => r.status === "accepted" || r.status === "already")
          .map((r) => keyOf(r.tenantId, r.recibidaId)),
      );
      // Roll back any rows that came back failed — un-fade them so the
      // accountant can see what's left.
      if (ctx?.startedKeys) {
        setAnimatingOut((prev) => {
          const next = new Set(prev);
          for (const k of ctx.startedKeys) {
            if (!acceptedKeys.has(k)) next.delete(k);
          }
          return next;
        });
      }
      // Drop succeeded keys from selection. Failed ones stay selected
      // so a retry is one click away.
      setSelected((prev) => {
        const next = new Set(prev);
        for (const k of acceptedKeys) next.delete(k);
        return next;
      });

      const acceptedCount = resp.acceptedCount + resp.alreadyCount;
      if (acceptedCount > 0 && resp.failedCount === 0) {
        toast.success(
          t("bandeja.acks.bulk.allOk", {
            count: acceptedCount,
            defaultValue: "Aceptaste {{count}} comprobante",
            defaultValue_plural: "Aceptaste {{count}} comprobantes",
          }),
        );
      } else if (acceptedCount > 0 && resp.failedCount > 0) {
        toast(
          t("bandeja.acks.bulk.partial", {
            ok: acceptedCount,
            failed: resp.failedCount,
            defaultValue:
              "Aceptaste {{ok}}; {{failed}} no se pudieron procesar",
          }),
        );
      } else {
        toast.error(
          t(
            "bandeja.acks.bulk.allFailed",
            "No pudimos aceptar los comprobantes seleccionados",
          ),
        );
      }

      // Refresh the queue. The animated-out rows will be replaced by
      // the next page of pending items.
      await queryClient.invalidateQueries({ queryKey: bandejaKeys.all });
    },
    onError: (_err, _vars, ctx) => {
      // Network or 5xx: clear every fade so the rows snap back into place.
      if (ctx?.startedKeys) {
        setAnimatingOut((prev) => {
          const next = new Set(prev);
          for (const k of ctx.startedKeys) next.delete(k);
          return next;
        });
      }
      toast.error(
        t(
          "bandeja.acks.bulk.networkError",
          "No pudimos conectar con el servidor. Probá de nuevo.",
        ),
      );
    },
  });

  // Count of currently-listed pending comprobantes per emisor WITHIN THE SAME
  // TENANT, so a row can offer "accept all from this emisor" scoped to the
  // client that row belongs to. The bandeja spans every tenant in the
  // accountant's cartera, and accepting enqueues a legally-binding ACKCFE to
  // DGI, so counting/accepting across tenants would silently ack a different
  // client's documents from a row that visually belongs to just one.
  const emisorCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of filteredItems) {
      const key = `${i.tenant.id}:${i.rucEmisor}`;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [filteredItems]);

  const acceptAllFromEmisor = useCallback(
    (tenantId: string, rucEmisor: string): void => {
      if (bulkMutation.isPending) return;
      const payloadItems = filteredItems
        .filter((i) => i.tenant.id === tenantId && i.rucEmisor === rucEmisor)
        .map((i) => ({ tenantId: i.tenant.id, recibidaId: i.recibidaId }));
      if (payloadItems.length === 0) return;
      bulkMutation.mutate({ items: payloadItems });
    },
    [filteredItems, bulkMutation],
  );

  const triggerBulkAccept = (): void => {
    if (effectiveSelectedCount === 0 || bulkMutation.isPending) return;
    // Filtered set only — selections sitting outside the current view
    // are intentionally excluded so the bulk action matches what the
    // accountant can see.
    const payloadItems = filteredItems
      .filter((i) => selected.has(keyOf(i.tenant.id, i.recibidaId)))
      .map((i) => ({ tenantId: i.tenant.id, recibidaId: i.recibidaId }));
    if (payloadItems.length === 0) return;
    bulkMutation.mutate({ items: payloadItems });
  };

  const triggerBulkExport = async (): Promise<void> => {
    if (effectiveSelectedCount === 0 || exporting) return;
    const payloadItems = filteredItems
      .filter((i) => selected.has(keyOf(i.tenant.id, i.recibidaId)))
      .map((i) => ({ tenantId: i.tenant.id, recibidaId: i.recibidaId }));
    if (payloadItems.length === 0) return;
    setExporting(true);
    try {
      const { blob, filename } = await accountantExportRecibidasZip(payloadItems);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(
        t(
          "bandeja.acks.bulk.exportError",
          "No pudimos exportar los XML. Probá de nuevo.",
        ),
      );
    } finally {
      setExporting(false);
    }
  };

  if (isLoading || !data) return <LoadingPage />;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t(
          "bandeja.acks.eyebrow",
          "Bandeja · comprobantes recibidos",
        )}
        title={BANDEJA_SECTIONS.acks.title}
        description={BANDEJA_SECTIONS.acks.description}
        onBack={() => navigate("/accountant/inbox")}
      />

      {items.length === 0 ? (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card className="border-[var(--fx-accent-300)]/40 bg-[var(--fx-accent-100)]/30">
            <CardContent className="flex items-center gap-3 py-8">
              <CheckCircle2
                className="h-5 w-5 text-[var(--fx-accent-500)]"
                aria-hidden
              />
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Sin comprobantes pendientes
                </div>
                <p className="text-xs text-muted-foreground">
                  Aceptaste o rechazaste todos los comprobantes recibidos
                  de tu cartera.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <>
          <BandejaFilterBar
            tenantOptions={tenantOptions}
            tenantFilter={tenantFilter}
            onTenantFilterChange={setTenantFilter}
            overdueOnly={overdueOnly}
            onToggleOverdue={toggleOverdueFilter}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            searchPlaceholder={t(
              "bandeja.acks.filters.searchPlaceholder",
              "Buscar cliente, emisor, CFE…",
            )}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            overdueLabel={t(
              "bandeja.acks.filters.overdue",
              "Vencidas",
            )}
            reduceMotion={Boolean(reduceMotion)}
          />

          {filteredItems.length === 0 ? (
            <motion.div
              key="filtered-empty"
              initial={reduceMotion ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)]/30">
                <CardContent className="flex items-center justify-between gap-3 py-6">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Receipt className="h-4 w-4" aria-hidden />
                    {t(
                      "bandeja.acks.filterEmpty",
                      "Ningún comprobante coincide con los filtros activos.",
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-8"
                  >
                    {t("bandeja.acks.clearFilters", "Limpiar filtros")}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <Card>
              <div className="flex items-center justify-between border-b border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)]/40 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={
                      allVisibleSelected
                        ? true
                        : someVisibleSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={toggleAllVisible}
                    aria-label={t(
                      "bandeja.acks.selectAll",
                      "Seleccionar todos los visibles",
                    )}
                  />
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Receipt className="h-3.5 w-3.5" aria-hidden />
                    <span>
                      {hasActiveFilters
                        ? t("bandeja.acks.showingFiltered", {
                            shown: filteredItems.length,
                            total: items.length,
                            defaultValue:
                              "Mostrando {{shown}} de {{total}} (filtrado)",
                          })
                        : t("bandeja.acks.showingOfTotal", {
                            shown: items.length,
                            total: data.total,
                            defaultValue: "Mostrando {{shown}} de {{total}}",
                          })}
                    </span>
                  </div>
                </div>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("bandeja.acks.bulk.hint", "Hacé clic en filas para seleccionar")}
                </span>
              </div>
              <div className="space-y-1 px-3 py-2">
                <BandejaRowList>
                  {filteredItems.map((item) => {
                    const key = keyOf(item.tenant.id, item.recibidaId);
                    return (
                      <BandejaRow
                        key={item.recibidaId}
                        kind="ack"
                        item={item}
                        withInlineActions
                        selection={{
                          selected: selected.has(key),
                          onToggle: () => toggleOne(key),
                        }}
                        bulkPending={animatingOut.has(key)}
                        emisorPendingCount={
                          emisorCounts.get(`${item.tenant.id}:${item.rucEmisor}`) ?? 1
                        }
                        onAcceptAllFromEmisor={() =>
                          acceptAllFromEmisor(item.tenant.id, item.rucEmisor)
                        }
                      />
                    );
                  })}
                </BandejaRowList>
              </div>
              {data.hasMore && !hasActiveFilters ? (
                <div className="border-t border-[var(--fx-ink-100)] px-4 py-3 text-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setLimit((l) =>
                        Math.min(PAGE_LIMIT_MAX, l + PAGE_LIMIT_INITIAL),
                      )
                    }
                    disabled={limit >= PAGE_LIMIT_MAX}
                  >
                    Cargar más
                  </Button>
                </div>
              ) : null}
            </Card>
          )}
        </>
      )}

      <AnimatePresence>
        {effectiveSelectedCount > 0 ? (
          <BulkActionBar
            selectedCount={effectiveSelectedCount}
            isWorking={bulkMutation.isPending}
            isExporting={exporting}
            onAccept={triggerBulkAccept}
            onExport={() => void triggerBulkExport()}
            onClear={clearSelection}
            reduceMotion={Boolean(reduceMotion)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/**
 * Sticky bottom-anchored bulk action bar. Slides up from below when
 * the selection is non-empty; springs back into view if dismissed.
 * Single primary action: bulk accept. Reject stays per-row because
 * each rejection needs its own motivos.
 */
function BulkActionBar({
  selectedCount,
  isWorking,
  isExporting,
  onAccept,
  onExport,
  onClear,
  reduceMotion,
}: {
  selectedCount: number;
  isWorking: boolean;
  isExporting: boolean;
  onAccept: () => void;
  onExport: () => void;
  onClear: () => void;
  reduceMotion: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={reduceMotion ? false : { y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { y: 24, opacity: 0 }}
      transition={{ type: "spring", stiffness: 350, damping: 30 }}
      className="pointer-events-none sticky bottom-4 z-30 mt-2 flex justify-center"
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-[var(--fx-ink-200)] bg-[var(--fx-ink-900)] px-3 py-2 text-[var(--fx-paper)] shadow-[0_18px_44px_-18px_rgba(22,21,20,0.55)]">
        <span className="pl-2 text-sm font-medium tabular-nums">
          {t("bandeja.acks.bulk.selectedCount", {
            count: selectedCount,
            defaultValue: "{{count}} seleccionada",
            defaultValue_plural: "{{count}} seleccionadas",
          })}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onExport}
          disabled={isWorking || isExporting}
          className="h-8 gap-1.5 rounded-full text-[var(--fx-paper)]/90 hover:bg-[var(--fx-s-raised-hover)]/10 hover:text-[var(--fx-paper)]"
        >
          {isExporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Download className="h-3.5 w-3.5" aria-hidden />
          )}
          {t("bandeja.acks.bulk.exportXml", "Exportar XML")}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onAccept}
          disabled={isWorking || isExporting}
          className="h-8 gap-1.5 rounded-full bg-[var(--fx-accent-300)] text-[var(--fx-ink-900)] hover:bg-[var(--fx-s-raised-hover)]"
        >
          {isWorking ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {t("bandeja.acks.bulk.working", "Aceptando…")}
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              {t("bandeja.acks.bulk.accept", {
                count: selectedCount,
                defaultValue: "Aceptar {{count}}",
              })}
            </>
          )}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClear}
          disabled={isWorking}
          className="h-8 gap-1 rounded-full text-[var(--fx-paper)]/70 hover:bg-[var(--fx-s-raised-hover)]/10 hover:text-[var(--fx-paper)]"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          {t("bandeja.acks.bulk.clear", "Limpiar")}
        </Button>
      </div>
    </motion.div>
  );
}

